import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  UserPlus,
  Loader2,
  Users,
  ShieldCheck,
  Building2,
  X,
  ToggleLeft,
  ToggleRight,
  KeyRound,
  Trash2,
} from "lucide-react";
import {
  adminListUsers,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
} from "@/lib/api";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pwdResetFor, setPwdResetFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    clinic_name: "",
    role: "user",
  });

  const reload = async () => {
    setLoading(true);
    try {
      const d = await adminListUsers();
      setUsers(d);
    } catch (e) {
      toast.error("Gagal memuat users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    setCreating(true);
    try {
      await adminCreateUser(form);
      toast.success(`User ${form.email} berhasil dibuat`);
      setShowCreate(false);
      setForm({ email: "", name: "", password: "", clinic_name: "", role: "user" });
      reload();
    } catch (e) {
      toast.error("Gagal membuat user", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 200),
      });
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await adminUpdateUser(u.id, { is_active: !u.is_active });
      toast.success(`${u.email} ${u.is_active ? "dinonaktifkan" : "diaktifkan"}`);
      reload();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || e.message).slice(0, 200));
    }
  };

  const resetPassword = async () => {
    if (!pwdResetFor) return;
    if (newPassword.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    try {
      await adminUpdateUser(pwdResetFor.id, { password: newPassword });
      toast.success(`Password ${pwdResetFor.email} direset`);
      setPwdResetFor(null);
      setNewPassword("");
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || e.message).slice(0, 200));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Hapus user ${u.email}? Data pemeriksaan mereka tidak ikut terhapus.`)) return;
    try {
      await adminDeleteUser(u.id);
      toast.success("User dihapus");
      reload();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || e.message).slice(0, 200));
    }
  };

  const adminCount = users.filter((u) => u.role === "admin").length;
  const clinicCount = users.filter((u) => u.role === "user").length;

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto">
      <div className="flex items-start lg:items-end justify-between gap-4 mb-8 flex-col lg:flex-row">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
            Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">
            User Management
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            {clinicCount} klinik pihak-3 · {adminCount} admin/radiolog
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="create-user-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 text-sm font-medium shadow-lg shadow-indigo-500/20 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Undang Klinik Baru
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-900 bg-zinc-900/40 overflow-hidden">
        {loading && (
          <div className="p-10 flex items-center justify-center text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Memuat...
          </div>
        )}
        {!loading && users.length === 0 && (
          <div className="p-16 text-center text-zinc-500">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            Belum ada user
          </div>
        )}
        {!loading && users.length > 0 && (
          <div className="divide-y divide-zinc-900">
            <div className="hidden md:grid md:grid-cols-[1fr_200px_160px_100px_100px_140px] gap-3 px-5 py-3 text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
              <div>User</div>
              <div>Clinic</div>
              <div>Role</div>
              <div>Exams</div>
              <div>Status</div>
              <div className="text-right">Actions</div>
            </div>
            {users.map((u) => (
              <div
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="grid grid-cols-1 md:grid-cols-[1fr_200px_160px_100px_100px_140px] gap-3 px-5 py-4 items-center"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-100 truncate">
                    {u.name || "—"}
                  </div>
                  <div className="text-xs font-mono text-zinc-500 truncate">{u.email}</div>
                </div>
                <div className="text-xs text-zinc-400 truncate flex items-center gap-1.5">
                  <Building2 className="w-3 h-3 text-zinc-500" />
                  {u.clinic_name || "-"}
                </div>
                <div>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${
                      u.role === "admin"
                        ? "bg-indigo-500/10 text-indigo-300 border-indigo-500/20"
                        : "bg-zinc-800 text-zinc-400 border-zinc-700"
                    }`}
                  >
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> admin
                      </span>
                    ) : (
                      "user"
                    )}
                  </span>
                </div>
                <div className="text-xs font-mono text-zinc-400">{u.exam_count ?? 0}</div>
                <div>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                      u.is_active
                        ? "bg-emerald-500/10 text-emerald-400"
                        : "bg-rose-500/10 text-rose-400"
                    }`}
                  >
                    {u.is_active ? "Active" : "Disabled"}
                  </span>
                </div>
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={() => setPwdResetFor(u)}
                    data-testid={`reset-pwd-${u.id}`}
                    className="p-1.5 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-100"
                    title="Reset password"
                  >
                    <KeyRound className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleActive(u)}
                    data-testid={`toggle-active-${u.id}`}
                    className="p-1.5 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-100"
                    title={u.is_active ? "Nonaktifkan" : "Aktifkan"}
                  >
                    {u.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => deleteUser(u)}
                    data-testid={`delete-user-${u.id}`}
                    className="p-1.5 rounded hover:bg-rose-500/10 text-zinc-500 hover:text-rose-400"
                    title="Hapus"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowCreate(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-semibold text-zinc-100">
                Undang Klinik / Admin
              </h2>
              <button onClick={() => setShowCreate(false)} className="text-zinc-500 hover:text-zinc-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submitCreate} className="space-y-3">
              <Field
                label="Email"
                type="email"
                required
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                testId="new-user-email"
              />
              <Field
                label="Nama"
                required
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testId="new-user-name"
              />
              <Field
                label="Password (min 6 karakter)"
                type="password"
                required
                value={form.password}
                onChange={(v) => setForm({ ...form, password: v })}
                testId="new-user-password"
              />
              <Field
                label="Nama Klinik"
                value={form.clinic_name}
                onChange={(v) => setForm({ ...form, clinic_name: v })}
                testId="new-user-clinic"
                placeholder="Klinik MCU Sehat"
              />
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                  Role
                </label>
                <select
                  data-testid="new-user-role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                >
                  <option value="user">Klinik / Pihak-3 (user)</option>
                  <option value="admin">Radiolog / Admin</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={creating}
                data-testid="submit-create-user-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-4 py-2.5 text-sm font-medium transition-colors"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Buat User
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Password reset modal */}
      {pwdResetFor && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPwdResetFor(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold text-zinc-100">Reset Password</h2>
              <button onClick={() => setPwdResetFor(null)} className="text-zinc-500 hover:text-zinc-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-sm text-zinc-400 mb-4">
              Reset password untuk <span className="text-zinc-100 font-mono">{pwdResetFor.email}</span>
            </div>
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Password baru (min 6 karakter)"
              data-testid="reset-password-input"
              className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none mb-3"
            />
            <button
              onClick={resetPassword}
              data-testid="submit-reset-password-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 text-sm font-medium transition-colors"
            >
              Reset Password
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
        {label}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
      />
    </div>
  );
}
